/**
 * @fileoverview Bank Connections API service using axios client and Zod validation.
 * @module features/bank-connections/api/bankConnectionsService
 */

import apiClient from '@/lib/apiClient'
import {
  bankConnectionsListSchema,
  banksListSchema,
  bankAuthStartResponseSchema,
  syncResultDtoSchema,
  type BankConnectionDto,
  type BankDto,
  type BankAuthStartRequest,
  type BankAuthStartResponse,
  type SyncResultDto,
} from '../schemas/bankConnectionsSchema'

const BASE_PATH = '/v1/bank-connections'

/**
 * List all bank connections for the current organization.
 *
 * @returns Promise resolving to array of bank connections
 * @throws Error if API validation fails
 */
export async function listBankConnections(): Promise<BankConnectionDto[]> {
  const response = await apiClient.get(BASE_PATH)
  const parsed = bankConnectionsListSchema.safeParse(response.data)
  if (!parsed.success) {
    console.error('[bankConnectionsService] Validation error:', parsed.error)
    throw new Error('API validation failed for /api/v1/bank-connections')
  }
  return parsed.data
}

/**
 * List available banks for a specific country.
 *
 * @param country ISO 3166-1 alpha-2 country code (e.g., 'PL')
 * @returns Promise resolving to array of available banks
 * @throws Error if API validation fails
 */
export async function listBanks(country: string): Promise<BankDto[]> {
  const response = await apiClient.get(`${BASE_PATH}/banks`, {
    params: { country },
  })
  const parsed = banksListSchema.safeParse(response.data)
  if (!parsed.success) {
    console.error('[bankConnectionsService] Validation error:', parsed.error)
    throw new Error('API validation failed for /api/v1/bank-connections/banks')
  }
  return parsed.data
}

/**
 * Start bank authorization flow.
 * Returns a URL to redirect the user to for bank authorization.
 *
 * @param request Bank name and country code
 * @returns Promise resolving to authorization URL
 * @throws Error if API validation fails
 */
export async function startBankAuthorization(
  request: BankAuthStartRequest
): Promise<BankAuthStartResponse> {
  const response = await apiClient.post(`${BASE_PATH}/authorize`, request)
  const parsed = bankAuthStartResponseSchema.safeParse(response.data)
  if (!parsed.success) {
    console.error('[bankConnectionsService] Validation error:', parsed.error)
    throw new Error('API validation failed for /api/v1/bank-connections/authorize')
  }
  return parsed.data
}

/**
 * Revoke (disconnect) a bank connection.
 *
 * @param id Connection ID (UUID)
 * @returns Promise resolving when connection is revoked
 */
export async function revokeBankConnection(id: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${id}`)
}

/**
 * Parameters for sync operations.
 */
export interface SyncParams {
  /** Start date (YYYY-MM-DD). Defaults to 30 days ago. */
  dateFrom?: string
  /** End date (YYYY-MM-DD). Defaults to today. */
  dateTo?: string
}

/**
 * Sync transactions for a specific bank connection.
 *
 * @param id Connection ID (UUID)
 * @param params Optional date range parameters
 * @returns Promise resolving to sync result
 * @throws Error if API validation fails
 */
export async function syncBankConnection(
  id: string,
  params?: SyncParams
): Promise<SyncResultDto> {
  const response = await apiClient.post(`${BASE_PATH}/${id}/sync`, null, {
    params: params ? { dateFrom: params.dateFrom, dateTo: params.dateTo } : undefined,
  })
  const parsed = syncResultDtoSchema.safeParse(response.data)
  if (!parsed.success) {
    console.error('[bankConnectionsService] Validation error:', parsed.error)
    throw new Error('API validation failed for sync operation')
  }
  return parsed.data
}
